import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { shouldRenderDesktop, isMobileDevice, isPWAInstalled } from '../utils/deviceDetection';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import formatChatTime from '../utils/formatChatTime';
import axios from 'axios';
import { toast } from 'sonner';
import AdminChat from './AdminChat';
import StatusManagement from '../components/StatusManagement';
import FlagIcons from '../components/FlagIcons';
import InstantAccessG from '../components/InstantAccessG';
import InstantAccessPT from '../components/InstantAccessPT';
import InstantAccessN from '../components/InstantAccessN';
import PendingManager from '../components/PendingManager';
import InquiryPanel from '../components/InquiryPanel';
import InstantAccessPW from '../components/InstantAccessPW';
import {
  Chats,
  Gear,
  Broom,
  SignOut,
  Users,
  Megaphone,
  UsersThree,
  Star,
  ArrowClockwise,
  ArrowLeft,
  Copy,
  WhatsappLogo,
  ChatCircle,
  ArrowsClockwise,
  DotsThreeVertical,
  ChatCircleDots,
  PushPin
} from '@phosphor-icons/react';
import MBSetupBadge from '../components/MBSetupBadge';
import { avatarPalFor } from '../utils/avatarPalette';
import { useMBList } from '../hooks/useMBList';
import BulkAssignMBModal from '../components/BulkAssignMBModal';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { startPolling, onVisibilityFetch } from '../utils/pollingKeepAlive';
import socket, { connectSocket, onMessagesRead, offMessagesRead, onNewMessage, offNewMessage } from '../utils/socket';
import CustomerDetailsPanel from '../components/CustomerDetailsPanel';
import { truncatePreview } from '../utils/previewTruncate';
import MobileWhatsAppAdminView from '../components/MobileWhatsAppAdminView';
import useSectionCycleKeys from '../hooks/useSectionCycleKeys';

// Batch H, point 3 — module-level so the array reference stays stable
// across renders (avoids re-attaching the cycle-key listeners every render).
const MAIN_SECTIONS = ['deposit', 'withdrawal', 'support', 'inquiry'];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification, playNotificationSound, updateAppBadge } = useWebNotification();
  const [activeTab, setActiveTab] = useState(() => {
    // Priority: location.state (fresh nav) > sessionStorage (page reload / hardware back) > default
    if (location.state?.activeTab) return location.state.activeTab;
    try {
      const saved = sessionStorage.getItem('admin_active_tab');
      if (saved && ['deposit', 'withdrawal', 'support', 'inquiry', 'archive'].includes(saved)) return saved;
    } catch (_) {}
    return 'deposit';
  });
  const [supportSubTab, setSupportSubTab] = useState('support'); // 'support' or 'inquiry'

  // Batch H — keyboard nav (Ctrl+D/W/S/I): GlobalKeyboardShortcuts.jsx
  // navigates here with `state.activeTab` when arriving from elsewhere
  // (handled by the useState initializer above); when already sitting on
  // this dashboard it instead dispatches this event so the tab switches
  // without a route change.
  useEffect(() => {
    const onJump = (e) => {
      if (['deposit', 'withdrawal', 'support', 'inquiry'].includes(e.detail)) {
        setActiveTab(e.detail);
      }
    };
    window.addEventListener('dws:kbnav-jump-section', onJump);
    return () => window.removeEventListener('dws:kbnav-jump-section', onJump);
  }, []);
  // Persist MB filter across chat-open → back navigation and page refreshes.
  const MB_FILTER_KEY_ADMIN = 'admin-mb-filter';
  const liveMBs = useMBList();
  const [mbFilter, setMbFilterState] = useState(() => {
    try { return sessionStorage.getItem(MB_FILTER_KEY_ADMIN) || 'all'; }
    catch { return 'all'; }
  }); // 'all' | 'MB7'..'MB11'
  const setMbFilter = React.useCallback((v) => {
    setMbFilterState(v);
    try {
      if (v && v !== 'all') sessionStorage.setItem(MB_FILTER_KEY_ADMIN, v);
      else sessionStorage.removeItem(MB_FILTER_KEY_ADMIN);
    } catch {}
  }, []);
  const [withdrawGroups, setWithdrawGroups] = useState([]);
  const [showWithdrawGroupsMenu, setShowWithdrawGroupsMenu] = useState(false);
  const [gMenuPos, setGMenuPos] = useState({ top: 0, left: 0 });

  // Load all MB-wise Withdraw Groups (admin sees all)
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/coin-cut/withdraw-groups`, { params: { user_id: user.id } })
      .then(res => setWithdrawGroups(res.data?.groups || []))
      .catch(() => {});
  }, [user]);
  const [showBulkMB, setShowBulkMB] = useState(false);
  const [rooms, setRooms] = useState(() => {
    // POINT 3 — persistent (localStorage) instant-cache so stale-but-visible
    // chat list survives full app restart (sessionStorage did not).
    return readInstantCache('admin_rooms', 'self') || [];
  });
  const [groups, setGroups] = useState(() => {
    return readInstantCache('admin_groups', 'self') || [];
  });
  const [filteredRooms, setFilteredRooms] = useState([]);
  // Progressive rendering — only paint first N rows initially, load more on scroll.
  // Keeps DOM light on production accounts with 300+ chats.
  const [visibleCount, setVisibleCount] = useState(30);
  // FIX 2 — track how many rooms we currently hold so loadRooms can tell a
  // background refresh from a first load (refresh must never shrink the list).
  const roomsLenRef = useRef(0);
  useEffect(() => { roomsLenRef.current = rooms.length; }, [rooms]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [loading, setLoading] = useState(() => {
    // If we have cached data, don't show loading spinner
    return !readInstantCache('admin_rooms', 'self');
  });
  const [unreadCounts, setUnreadCounts] = useState({}); // {room_id: count}
  const [appName, setAppName] = useState('DWS Vault Chat');
  const [inquiryAnalytics, setInquiryAnalytics] = useState({ today: 0, last_7_days: 0, total: 0 });
  const [inquiryList, setInquiryList] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [inquiryFilter, setInquiryFilter] = useState('today'); // 'today', '7days', 'total'
  
  // Selection and deletion states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showClientListModal, setShowClientListModal] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState(null);
  // Point 55 — handle to the document-level multi-touch guard registered
  // while a long-press timer is pending (see handleLongPressStart).
  const longPressGlobalGuardRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(() => shouldRenderDesktop());
  // Web redesign (desktop) — resizable chat-list width, persisted per logged-in
  // session (cleared on logout via AuthContext). Range 280–680px.
  const [listWidth, setListWidth] = useState(() => {
    try { const v = parseInt(localStorage.getItem('dws_list_width'), 10); if (v >= 280 && v <= 680) return v; } catch (_) {}
    return 380;
  });
  const resizeRef = useRef(null);
  const [showMbDropdown, setShowMbDropdown] = useState(false);
  const startListResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = listWidth;
    let latest = startW;
    const onMove = (ev) => {
      latest = Math.min(680, Math.max(280, startW + (ev.clientX - startX)));
      setListWidth(latest);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      try { localStorage.setItem('dws_list_width', String(latest)); } catch (_) {}
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const [selectedChatId, setSelectedChatId] = useState(location.state?.selectedChatId || null);

  // Auto-close the Customer Details 3rd panel when the active chat/panel changes.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('dws:close-customer-details'));
    // Expose the open chat for InstantAccessG / group shortcuts so the group
    // back-button re-opens THIS chat instead of closing the right-side panel.
    try { sessionStorage.setItem('dws_open_chat_id', selectedChatId || ''); } catch (_) {}
  }, [selectedChatId]);


  // Batch H, point 3 — bare Ctrl / bare Tab cycles Deposit→Withdraw→
  // Support→Inquiry, only while sitting at one of those main pages (no
  // chat open — on desktop that's !selectedChatId; on mobile, opening a
  // chat navigates to a different route/component entirely, which
  // naturally unmounts this listener).
  const onSectionCycle = React.useCallback((next) => setActiveTab(next), []);
  useSectionCycleKeys(
    MAIN_SECTIONS,
    activeTab,
    onSectionCycle,
    !selectedChatId && MAIN_SECTIONS.includes(activeTab)
  );

  const chatListScrollRef = React.useRef(null);
  const chatListLoadMoreRef = React.useRef(null);
  const scrollRestoredRef = React.useRef(false);
  const persistScroll = React.useCallback(() => {
    try {
      if (chatListScrollRef.current) sessionStorage.setItem('admin_chat_list_scroll', String(chatListScrollRef.current.scrollTop));
    } catch {}
  }, []);
  React.useEffect(() => {
    if (scrollRestoredRef.current) return;
    const el = chatListScrollRef.current; if (!el) return;
    try {
      const saved = parseInt(sessionStorage.getItem('admin_chat_list_scroll') || '0', 10);
      if (saved > 0) requestAnimationFrame(() => { el.scrollTop = saved; });
      scrollRestoredRef.current = true;
    } catch {}
  }, [rooms?.length]);
  const [bottomTab, setBottomTab] = useState('chats');
  // Point 3 — silent auto-reload replaced by a controllable UpdateBanner popup
  // (mounted globally in App.js). No forced reload here anymore.
  const [showMenu, setShowMenu] = useState(false);
  const [showPendingManager, setShowPendingManager] = useState(false);
  const [copyPopupChat, setCopyPopupChat] = useState(null);
  const [filterTab, setFilterTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_filter_tab');
      if (saved && ['all', 'unread', 'favourites', 'groups'].includes(saved)) return saved;
    } catch (_) {}
    return 'all';
  });
  const menuRef = useRef(null);
  
  // Notification tracking
  const prevUnreadCountsRef = useRef({});
  const isFirstLoadRef = useRef(true);
  const notifiedRoomsRef = useRef(new Set());
  // Issue 16 — group unread tracking (sound/notification + app badge).
  const prevGroupUnreadRef = useRef({});
  const isFirstGroupLoadRef = useRef(true);

  // Issue 16 — app badge = unread 1-on-1 chats + unread groups.
  useEffect(() => {
    const oneOnOne = Object.values(unreadCounts).filter(c => c > 0).length;
    const grp = (groups || []).filter(g => (g.unread_count || 0) > 0).length;
    updateAppBadge(oneOnOne + grp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCounts, groups]);

  // Issue 16 — sound + notification when any group's unread count rises
  // (suppressed on first load / baseline). Group chat opens on a separate
  // /groups/:id page, so the dashboard is never "inside" a group.
  useEffect(() => {
    const cur = {};
    (groups || []).forEach(g => { cur[g.id] = g.unread_count || 0; });
    if (isFirstGroupLoadRef.current) {
      isFirstGroupLoadRef.current = false;
      prevGroupUnreadRef.current = cur;
      return;
    }
    const prev = prevGroupUnreadRef.current;
    (groups || []).forEach(g => {
      if ((g.unread_count || 0) > (prev[g.id] || 0)) {
        showNotification({
          title: g.name || 'Group',
          body: 'New group message',
          tag: `admin-group-${g.id}-${Date.now()}`,
          data: { groupId: g.id, isGroup: true },
        });
      }
    });
    prevGroupUnreadRef.current = cur;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Update selectedChatId when navigating from ClientList
  useEffect(() => {
    if (location.state?.selectedChatId) {
      setSelectedChatId(location.state.selectedChatId);
    }
  }, [location.state]);

  // Persist active tab across mounts so mobile back-navigation lands on the same tab
  useEffect(() => {
    try { sessionStorage.setItem('admin_active_tab', activeTab); } catch (_) {}
  }, [activeTab]);

  // Persist filter tab (All / Unread / Favourites / Groups) across back-nav
  useEffect(() => {
    try { sessionStorage.setItem('admin_filter_tab', filterTab); } catch (_) {}
  }, [filterTab]);

  // Real-time cross-user unread sync: when any user marks a message read
  // (or a staff reply auto-clears the room), instantly zero the badge here.
  useEffect(() => {
    if (!user?.id) return;
    const handler = (payload) => {
      const rid = payload?.room_id;
      if (!rid) return;
      setUnreadCounts(prev => (prev[rid] ? { ...prev, [rid]: 0 } : prev));
      setRooms(prev => prev.map(r => r.id === rid ? { ...r, unread_count: 0 } : r));
    };
    onMessagesRead(handler);
    return () => offMessagesRead(handler);
  }, [user?.id]);

  // Desktop detection (disabled in PWA mode + touch devices)
  useEffect(() => {
    const handleResize = () => setIsDesktop(shouldRenderDesktop());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile/PWA has NO inline 'groups' tab — groups live on the dedicated
  // /groups screen (the mobile Groups chip navigates there). If we arrive with
  // activeTab='groups' (e.g. pressing back from an MB withdraw group chat) the
  // desktop layout would wrongly render ("web version" bug). Redirect to the
  // mobile groups list instead.
  useEffect(() => {
    if (!isDesktop && activeTab === 'groups') {
      navigate('/groups', { replace: true });
    }
  }, [isDesktop, activeTab, navigate]);

  // Fetch app name from settings
  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API}/settings/support-info`);
        if (response.data.app_name) {
          setAppName(response.data.app_name);
          document.title = `${response.data.app_name} - Admin`;
        }
      } catch (error) {
        console.error('Failed to fetch app name:', error);
      }
    };
    
    fetchAppName();
    // Poll every 10 seconds
    const interval = setInterval(fetchAppName, 10000);
    return () => clearInterval(interval);
  }, []);


  // ========== ONLINE STATUS HEARTBEAT ==========
  // Mark admin as online when on dashboard (critical for delivery status)
  useEffect(() => {
    if (!user?.id) return;
    
    // Connect socket for real-time notifications
    connectSocket(user.id);
    
    // Set online immediately when dashboard loads
    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('❌ Online status update failed:', err));
    
    // Slow heartbeat every 30s — socket presence handles online/offline.
    const heartbeatInterval = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('❌ Heartbeat failed:', err));
    }, 30000);
    
    // No is_online=false flip on unmount — socket disconnect grace owns it.
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [user?.id]);

  useEffect(() => {
    loadRooms();
    loadUnreadCounts();
    // Background-safe polling using Web Worker — slowed to 30s now that the
    // socket `new_message` handler (below) does the real-time update.
    // Session-40 (Task 2 Item 2): tightened poll interval from 30s → 3s so
    // new customer messages / enquiries reflect near-instantly on the admin
    // panel even if the socket event was dropped or the tab wasn't in focus.
    // Web-worker driven, so background tab throttling doesn't touch it.
    // Phase B (real-time dashboards) — the socket `new_message` + `messages_read`
    // handlers now update the chat list & unread counts INSTANTLY, so this poll
    // is only a safety-net for brand-new rooms / dropped socket events. Slowed
    // 3s → 15s to cut DB load ~5x at 200-300 concurrent staff (onVisibilityFetch
    // still refetches the moment the tab regains focus).
    const stopPoll = startPolling('admin-dashboard', () => {
      loadRooms();
      loadUnreadCounts();
    }, 15000, { skipWhenHidden: true });
    const cleanupVis = onVisibilityFetch(() => {
      loadRooms();
      loadUnreadCounts();
    });
    return () => { stopPoll(); cleanupVis(); };
  // Runs once when user id is known. Tab switching does NOT refetch — rooms
  // are the same dataset for every tab, only client-side filter changes.
  }, [user?.id]);

  // LIVE chat list sync via Socket.IO — every incoming message immediately
  // updates the affected room's last_message + last_message_time in local
  // state so the WhatsApp-style sort reorders and shows the correct preview
  // without waiting for the 30s polling tick.
  const selectedChatIdRef = useRef(selectedChatId);
  useEffect(() => { selectedChatIdRef.current = selectedChatId; }, [selectedChatId]);
  useEffect(() => {
    if (!user?.id) return;

    const previewFor = (msg) => {
      if (!msg) return '';
      if (msg.is_sticker) return '🎨 Sticker';
      if (msg.voice_url) return '🎤 Voice message';
      if (msg.image_url) return '📷 Photo';
      if (msg.document_url) return '📄 ' + (msg.document_name || 'Document');
      if (msg.call_info) return '📞 Voice call';
      return (msg.text || '').slice(0, 40);
    };

    const handleNewMessage = (data) => {
      if (!data?.message || !data.room_id) return;
      const msg = data.message;
      const roomId = data.room_id;
      setRooms(prev => {
        let touched = false;
        const next = prev.map(r => {
          if (r.id !== roomId && r.room_id !== roomId) return r;
          touched = true;
          return {
            ...r,
            last_message: previewFor(msg),
            last_message_time: msg.timestamp || new Date().toISOString(),
            last_message_sender_id: msg.sender_id,
            last_message_sender_role: msg.sender_role,
            unread_count: (msg.sender_id !== user.id && selectedChatIdRef.current !== roomId)
              ? (r.unread_count || 0) + 1
              : r.unread_count,
          };
        });
        return touched ? next : prev;
      });
      // FIX (June-2026): also bump the `unreadCounts` MAP — the green unread
      // badge + filter chips read from this map, not from room.unread_count.
      // Previously only the room object was bumped, so admins saw NO real-time
      // unread count / green badge (handlers had a `|| chat.unread_count`
      // fallback; admin didn't). Read/open already clear this map (lines ~297,
      // ~743), so incrementing here keeps it correct both ways.
      if (msg.sender_id !== user.id && selectedChatIdRef.current !== roomId) {
        setUnreadCounts(prev => ({ ...prev, [roomId]: (prev[roomId] || 0) + 1 }));
      }
    };

    onNewMessage(handleNewMessage);
    return () => offNewMessage(handleNewMessage);
  }, [user?.id]);

  // P18/P19 — realtime SR badges. Refresh the chat list (pending-SR pill 🎫)
  // + unread counts the instant a service_request_* event fires, rather than
  // waiting for the 15s poll (was the 5-10 min stuck-count). Debounced.
  useEffect(() => {
    if (!user?.id) return;
    let h = null;
    const onSR = () => {
      if (h) clearTimeout(h);
      h = setTimeout(() => { loadRooms(); loadUnreadCounts(); }, 350);
    };
    socket.on('service_request_created', onSR);
    socket.on('service_request_closed', onSR);
    socket.on('service_request_cancelled', onSR);
    socket.on('service_request_accepted', onSR);
    return () => {
      if (h) clearTimeout(h);
      socket.off('service_request_created', onSR);
      socket.off('service_request_closed', onSR);
      socket.off('service_request_cancelled', onSR);
      socket.off('service_request_accepted', onSR);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Inquiry data has its own reload cadence (depends on selectedDate/filter)
  useEffect(() => {
    if (activeTab === 'inquiry') loadInquiryData();
  }, [activeTab, selectedDate, inquiryFilter]);

  const loadInquiryData = async () => {
    try {
      // Load analytics
      const analyticsResponse = await axios.get(`${API}/inquiry/analytics`);
      // Smart setState for inquiry analytics
      setInquiryAnalytics(prev => {
        if (JSON.stringify(prev) === JSON.stringify(analyticsResponse.data)) return prev;
        return analyticsResponse.data;
      });
      
      // Load inquiry list based on filter or date
      let listParams = {};
      if (selectedDate) {
        listParams = { date: selectedDate };
      } else if (inquiryFilter === 'today') {
        listParams = { date: new Date().toISOString().split('T')[0] };
      } else if (inquiryFilter === '7days') {
        listParams = { days: 7 };
      }
      // 'total' means no filter - get all
      
      const listResponse = await axios.get(`${API}/inquiry/list`, { params: listParams });
      setInquiryList(prev => {
        const newList = listResponse.data.inquiries || [];
        if (JSON.stringify(prev) === JSON.stringify(newList)) return prev;
        return newList;
      });
    } catch (error) {
      console.error('Failed to load inquiry data:', error);
    }
  };

  // Reload inquiry list when date or filter changes
  useEffect(() => {
    if (activeTab === 'inquiry') {
      loadInquiryData();
    }
  }, [selectedDate, inquiryFilter]);

  const loadRooms = async (opts = {}) => {
    const { retry = 1 } = opts;
    try {
      const isRefresh = roomsLenRef.current > 0;  // FIX 2 — never shrink on refresh
      const PAGE = 500;
      // Page 0 — instant first paint (no more single giant payload / 2000 cap).
      const first = await axios.get(`${API}/chat/rooms`, {
        params: { user_id: user.id, role: user.role, limit: PAGE, offset: 0 }
      });
      let acc = Array.isArray(first.data) ? first.data : (first.data.rooms || []);
      let hasMore = Array.isArray(first.data) ? false : !!first.data.has_more;
      if (!isRefresh) {
        setRooms(prev => {
          if (JSON.stringify(prev) === JSON.stringify(acc)) return prev;
          writeInstantCache('admin_rooms', 'self', acc);
          return acc;
        });
      }
      
      // Fetch groups
      const groupsResponse = await axios.get(`${API}/groups/user-groups`, {
        params: { user_id: user.id }
      });
      setGroups(prev => {
        const newData = groupsResponse.data || [];
        if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
        writeInstantCache('admin_groups', 'self', newData);
        return newData;
      });
      
      setLoading(false);

      // Stream the REMAINING rooms in 50-chunks in the background so the old
      // 2000-room hard cap is gone and the first paint isn't blocked by one
      // huge payload. All rooms still land client-side, so search / filter tabs
      // / unread badges stay 100% accurate. (unread lives in the separate
      // `unreadCounts` map, so replacing `rooms` here never drops a live count.)
      let offset = (first.data && first.data.next_offset != null) ? first.data.next_offset : acc.length;
      let guard = 0;
      while (hasMore && guard < 500) {
        guard += 1;
        let res;
        try {
          res = await axios.get(`${API}/chat/rooms`, {
            params: { user_id: user.id, role: user.role, limit: PAGE, offset }
          });
        } catch (_) { break; }
        const chunk = Array.isArray(res.data) ? res.data : (res.data.rooms || []);
        if (!chunk.length) break;
        const seen = new Set(acc.map(r => r.id));
        acc = acc.concat(chunk.filter(r => !seen.has(r.id)));
        hasMore = Array.isArray(res.data) ? false : !!res.data.has_more;
        offset = (res.data && res.data.next_offset != null) ? res.data.next_offset : offset + chunk.length;
        if (!isRefresh) { writeInstantCache('admin_rooms', 'self', acc); setRooms(acc); }
      }
      // FIX 2 — apply the complete list once (on refresh this is the only
      // setRooms, so the list never shrinks to page 1 mid-poll).
      setRooms(prev => {
        if (JSON.stringify(prev) === JSON.stringify(acc)) return prev;
        writeInstantCache('admin_rooms', 'self', acc);
        return acc;
      });
    } catch (error) {
      console.error('Failed to load rooms:', error);
      // One silent retry — dead TCP sockets after backgrounding are the most
      // common cause and the second request always succeeds on a fresh conn.
      if (retry > 0 && !opts._retried) {
        setTimeout(() => loadRooms({ retry: retry - 1, _retried: true }), 800);
        return;
      }
      setLoading(false);
      // Only toast if we don't already have cached rooms — otherwise stale
      // data is still visible and the periodic poll will heal in the bg.
      try {
        const hasCache = readInstantCache('admin_rooms', 'self');
        if (!hasCache) toast.error('Could not load chats — check network');
      } catch {}
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, {
        params: { user_id: user.id }
      });
      
      // Convert array to object: {room_id: count}
      const countsMap = {};
      if (response.data.unread_rooms) {
        response.data.unread_rooms.forEach(item => {
          countsMap[item.room_id] = item.count;
        });
      }
      
      // ========== NOTIFICATION LOGIC ==========
      // Load notified rooms from localStorage (persist across navigation)
      const storedNotified = localStorage.getItem('admin_notified_rooms');
      if (storedNotified && notifiedRoomsRef.current.size === 0) {
        // Only load from localStorage on first load
        try {
          const parsed = JSON.parse(storedNotified);
          parsed.forEach(roomId => notifiedRoomsRef.current.add(roomId));
        } catch (e) {
          // Invalid JSON, ignore
        }
      }
      
      // Check for NEW unread messages and trigger notification
      if (isFirstLoadRef.current) {
        console.log('📋 First load - skipping notification, setting baseline');
        isFirstLoadRef.current = false;
      } else {
        const prevCounts = prevUnreadCountsRef.current;
        console.log('🔍 Checking for new messages...');
        
        Object.keys(countsMap).forEach(roomId => {
          const currentCount = countsMap[roomId];
          const prevCount = prevCounts[roomId] || 0;
          
          // New unread message detected - but not for the chat we're actively viewing
          if (currentCount > prevCount && roomId !== selectedChatId) {
            console.log(`🔔 NEW MESSAGE! Room ${roomId.substring(0,8)}... : prev=${prevCount}, new=${currentCount}`);
            
            // Find room details for better notification
            const room = rooms.find(r => r.id === roomId);
            const customerName = room?.customer_name || room?.customer?.username || 'Customer';
            const chatType = room?.chat_type || 'chat';
            
            // Show notification with sound - ALWAYS play sound for new message
            showNotification({
              title: `${customerName} - ${chatType.toUpperCase()}`,
              body: room?.last_message || 'New message',
              tag: `admin-dashboard-${roomId}-${Date.now()}`, // Unique tag to ensure new notification
              data: {
                roomId: roomId,
                chatType: chatType
              }
            });
          }
        });
      }
      
      // Update prev counts ref AFTER notification check
      prevUnreadCountsRef.current = { ...countsMap };
      
      setUnreadCounts(countsMap);
      // Issue 16 — app badge is now computed in a dedicated effect that
      // combines 1-on-1 unread chats with unread groups.
    } catch (error) {
      console.error('Failed to load unread counts:', error);
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter rooms based on debounced search
  useEffect(() => {
    let filtered = [];
    
    if (activeTab === 'inquiry') {
      // Session-49: On mobile, the Inquiry slide shows chat_type='inquiry'
      // rooms. On desktop, the Inquiry tab renders analytics section instead
      // (see `activeTab === 'inquiry' ?` branch below) so populating this
      // list has no visual effect there — it just future-proofs the state.
      filtered = rooms.filter(room => room.chat_type === 'inquiry' && !room.is_archived);
    } else if (activeTab === 'archive') {
      filtered = rooms.filter(room => room.is_archived === true);
    } else if (activeTab === 'support') {
      // Session-49: On mobile we removed the Enquiry sub-chip — Support slide
      // must show ONLY chat_type='support'. Desktop still has the sub-tabs
      // toggle (Support Normal / Inquiry Support) driven by supportSubTab.
      if (!isDesktop) {
        filtered = rooms.filter(room => room.chat_type === 'support' && !room.is_archived);
      } else if (supportSubTab === 'support') {
        filtered = rooms.filter(room => room.chat_type === 'support' && !room.is_archived);
      } else {
        filtered = rooms.filter(room => room.chat_type === 'inquiry' && !room.is_archived);
      }
    } else {
      filtered = rooms.filter(room => room.chat_type === activeTab && !room.is_archived);
    }
    
    // Apply filter tab
    if (filterTab === 'unread') {
      filtered = filtered.filter(room => unreadCounts[room.id] > 0);
    } else if (filterTab === 'favourites') {
      filtered = filtered.filter(room => room.is_favourite);
    } else if (filterTab === 'groups') {
      // Groups are separate from rooms - use groups array
      filtered = groups.map(g => ({
        ...g,
        id: g.id,
        customer_name: g.name,
        chat_type: 'groups',
        is_group: true,
        updated_at: g.updated_at || g.created_at
      }));
    }
    
    // Apply search filter if present — matches name, phone, IFSC, city,
    // state, district, branch and bank name (universal IFSC/address search).
    if (debouncedSearchQuery) {
      const qLower = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(room => {
        const a = room.customer_client_address || {};
        return (
          room.customer_name?.toLowerCase().includes(qLower) ||
          room.customer_phone?.includes(debouncedSearchQuery) ||
          (room.customer_ifsc_code || '').toLowerCase().includes(qLower) ||
          (room.customer_client_id || '').toLowerCase().includes(qLower) ||
          (a.ifsc || '').toLowerCase().includes(qLower) ||
          (a.city || '').toLowerCase().includes(qLower) ||
          (a.state || '').toLowerCase().includes(qLower) ||
          (a.district || '').toLowerCase().includes(qLower) ||
          (a.branch || '').toLowerCase().includes(qLower) ||
          (a.bank || '').toLowerCase().includes(qLower)
        );
      });
    }

    // MB Setup filter — applies only within withdrawal tab
    if (['deposit', 'withdrawal', 'support'].includes(activeTab) && filterTab !== 'groups' && mbFilter !== 'all') {
      filtered = filtered.filter(room => room.mb_setup === mbFilter);
    }

    // Update filtered rooms (React shallow-compares arrays and will skip
    // re-render of children whose props/keys are unchanged).
    // WhatsApp-style sort: most-recent last_message_time (or falling back to
    // updated_at / created_at) on top. Handles legacy rows missing the `Z`
    // timezone marker so parsing stays deterministic.
    const parseTs = (r) => {
      const raw = r.last_message_time || r.updated_at || r.created_at || '';
      if (!raw) return 0;
      const norm = typeof raw === 'string' && !/[Zz]|[+-]\d\d:?\d\d$/.test(raw) ? raw + 'Z' : raw;
      const t = new Date(norm).getTime();
      return isNaN(t) ? 0 : t;
    };
    filtered.sort((a, b) => parseTs(b) - parseTs(a));
    setFilteredRooms(filtered);
  }, [debouncedSearchQuery, rooms, groups, activeTab, supportSubTab, filterTab, unreadCounts, mbFilter, isDesktop]);

  // Reset the progressive-render window to 30 ONLY when the user actually
  // changes a filter / tab / search — never on background data or socket
  // updates. Previously this lived inside the sort/filter effect above, so it
  // fired on every `rooms` change: the slow-DB 50-chunk background stream and
  // each incoming socket message collapsed the list back to 30 rows mid-scroll,
  // making it jump to the top and sometimes show only a couple of chats.
  useEffect(() => {
    setVisibleCount(30);
  }, [debouncedSearchQuery, activeTab, supportSubTab, filterTab, mbFilter]);

  // Progressive-render observer — bumps visibleCount by 30 when the sentinel
  // scrolls into view, so DOM stays light on 300+ chat accounts.
  useEffect(() => {
    const el = chatListLoadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setVisibleCount(c => Math.min(c + 30, filteredRooms.length));
      }
    }, { root: null, rootMargin: '200px', threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [filteredRooms.length, visibleCount]);


  const handleChatClick = (roomId) => {
    // Optimistic: clear unread badge instantly so no stale count flashes on back-nav
    setUnreadCounts(prev => (prev[roomId] ? { ...prev, [roomId]: 0 } : prev));
    setRooms(prev => prev.map(r => r.id === roomId ? { ...r, unread_count: 0 } : r));
    if (isDesktop) {
      setSelectedChatId(roomId);
    } else {
      // Stamp the current tab into the dashboard's history entry BEFORE
      // pushing the chat route, so the browser/OS back button (which uses
      // `navigate(-1)`) returns the user to the same tab they came from.
      navigate(location.pathname, { replace: true, state: { ...(location.state || {}), activeTab } });
      navigate(`/admin-chat/${roomId}`, { state: { activeTab } });
    }
  };

  const handleGroupClick = (groupId) => {
    // Carry `from` + `fromState` (like the withdraw-group dropdown does) so
    // GroupChatPage's back button returns to THIS dashboard AND re-opens the
    // 1-on-1 chat that was open (selectedChatId), instead of dropping to the
    // groups list.
    const from = window.location.pathname + window.location.search + window.location.hash;
    navigate(location.pathname, { replace: true, state: { ...(location.state || {}), activeTab } });
    navigate(`/groups/${groupId}`, { state: { from, fromState: { selectedChatId, activeTab } } });
  };

  const toggleFavourite = async (roomId, currentStatus) => {
    try {
      await axios.post(`${API}/chat/rooms/${roomId}/favourite`, {
        user_id: user.id
      });
      
      // Update local state
      setRooms(prevRooms => 
        prevRooms.map(room => 
          room.id === roomId 
            ? { ...room, is_favourite: !currentStatus }
            : room
        )
      );
      
      toast.success(currentStatus ? 'Removed from favourites' : 'Added to favourites');
    } catch (error) {
      console.error('Toggle favourite error:', error);
      toast.error('Failed to update favourite');
    }
  };

  // Copy client bank details - show popup
  const copyClientDetails = async (room) => {
    setCopyPopupChat(room);
  };

  // Open WhatsApp with client number (PWA-compatible)
  const openWhatsApp = (phone, roomId) => {
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    // Staff-only audit note in the chat (customer never sees it) — logs which
    // handler/admin redirected to the customer's WhatsApp. Fire-and-forget.
    if (roomId) {
      axios.post(`${API}/chat/room/${roomId}/call-log`, { user_id: user?.id, kind: 'whatsapp' }).catch(() => {});
    }
    const cleanNumber = phone.replace(/\D/g, '');
    const whatsappNumber = cleanNumber.startsWith('91') ? cleanNumber : `91${cleanNumber}`;
    const link = document.createElement('a');
    link.href = `https://wa.me/${whatsappNumber}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = () => {
    logout(); // Will handle redirect automatically
  };

  // Long-press handlers for selection mode
  const handleLongPressStart = (roomId, e) => {
    // Multi-touch guard — a 3-finger screenshot gesture briefly fires
    // touchstart. Don't arm the long-press timer if more than one finger
    // is already down.
    if (e.touches && e.touches.length > 1) return;

    // Get touch/mouse position
    const pos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
    setTouchStartPos(pos);

    // 1500ms so multi-finger system gestures don't accidentally trip us.
    const timer = setTimeout(() => {
      setSelectionMode(true);
      setSelectedRooms([roomId]);
      clearLongPressGlobalGuard();
    }, 1500);
    setLongPressTimer(timer);

    // Point 55 — the per-row touchstart/touchmove checks only see a finger
    // landing/moving on THIS row's own element. A real 3-finger screenshot
    // gesture usually lands its 2nd/3rd finger elsewhere on the screen, not
    // on the same row, so those checks can miss it if the first finger stays
    // still. Catch that case with a document-level listener that fires the
    // instant ANY 2nd touch appears anywhere, regardless of target.
    if (e.touches) {
      const guard = (ev) => {
        if (ev.touches && ev.touches.length > 1) {
          clearTimeout(timer);
          setLongPressTimer(null);
          setTouchStartPos(null);
          clearLongPressGlobalGuard();
        }
      };
      longPressGlobalGuardRef.current = guard;
      document.addEventListener('touchstart', guard, { passive: true });
    }
  };

  const clearLongPressGlobalGuard = () => {
    if (longPressGlobalGuardRef.current) {
      document.removeEventListener('touchstart', longPressGlobalGuardRef.current);
      longPressGlobalGuardRef.current = null;
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setTouchStartPos(null);
    clearLongPressGlobalGuard();
  };

  const handleTouchMove = (e) => {
    // Second finger arrived during the touch — likely a screenshot gesture.
    if (e.touches && e.touches.length > 1) {
      if (longPressTimer) { clearTimeout(longPressTimer); setLongPressTimer(null); }
      setTouchStartPos(null);
      clearLongPressGlobalGuard();
      return;
    }
    if (!touchStartPos || !longPressTimer) return;

    // Get current position
    const currentPos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };

    // Calculate distance moved
    const distance = Math.sqrt(
      Math.pow(currentPos.x - touchStartPos.x, 2) +
      Math.pow(currentPos.y - touchStartPos.y, 2)
    );

    // 15px tolerance — forgiving of finger jiggle during multi-finger gestures.
    if (distance > 15) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
      setTouchStartPos(null);
      clearLongPressGlobalGuard();
    }
  };

  const toggleRoomSelection = (roomId) => {
    if (selectedRooms.includes(roomId)) {
      setSelectedRooms(prev => prev.filter(id => id !== roomId));
    } else {
      setSelectedRooms(prev => [...prev, roomId]);
    }
  };

  const handleDeleteRooms = async () => {
    try {
      if (selectedRooms.length === 1) {
        await axios.delete(`${API}/chat/room/${selectedRooms[0]}?user_id=${user.id}`);
      } else {
        await axios.post(`${API}/chat/rooms/bulk-delete`, {
          room_ids: selectedRooms,
          user_id: user.id
        });
      }
      
      setRooms(prev => prev.filter(r => !selectedRooms.includes(r.id)));
      setSelectionMode(false);
      setSelectedRooms([]);
      setShowDeleteConfirm(false);
      
    } catch (error) {
      console.error('Failed to delete rooms:', error);
      toast.error('Failed to delete chats. Please try again.');
    }
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedRooms([]);
  };


  const handleToggleFavourite = async () => {
    try {
      // Backend expects { user_id } in the JSON body; passing `null` throws 422.
      for (const roomId of selectedRooms) {
        await axios.post(`${API}/chat/rooms/${roomId}/favourite`, { user_id: user.id });
      }
      
      // Optimistic local update — flip is_favourite on each selected room so the
      // list updates instantly without waiting for the next 3s poll.
      setRooms(prev => prev.map(r => (
        selectedRooms.includes(r.id) ? { ...r, is_favourite: !r.is_favourite } : r
      )));
      toast.success('Favourite updated');
      setSelectionMode(false);
      setSelectedRooms([]);
    } catch (error) {
      console.error('Failed to toggle favourite:', error);
      toast.error('Failed to toggle favourite');
    }
  };

  const handleToggleArchive = async () => {
    try {
      const isUnarchiving = activeTab === 'archive';
      
      for (const roomId of selectedRooms) {
        await axios.post(`${API}/chat/rooms/${roomId}/archive`, null, {
          params: { user_id: user.id, unarchive: isUnarchiving }
        });
      }
      
      toast.success(isUnarchiving ? 'Chats unarchived' : 'Chats archived');
      
      // Reload rooms to reflect changes
      loadRooms();
      setSelectionMode(false);
      setSelectedRooms([]);
    } catch (error) {
      console.error('Failed to toggle archive:', error);
      toast.error('Failed to archive chats');
    }
  };

  const handleTogglePinnedChat = async () => {
    try {
      // Point 53 — same "pin selected / unpin if all already pinned" logic
      // as the handler dashboard.
      const allPinned = selectedRooms.every(rid => rooms.find(r => r.id === rid)?.is_pinned_chat);
      await Promise.all(selectedRooms.map(async (roomId) => {
        const isPinned = rooms.find(r => r.id === roomId)?.is_pinned_chat;
        if (allPinned === isPinned) {
          await axios.post(`${API}/chat/rooms/${roomId}/pin-chat`, {});
        }
      }));
      toast.success(allPinned ? 'Unpinned' : 'Pinned to top');
      loadRooms();
      setSelectionMode(false);
      setSelectedRooms([]);
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update pin');
    }
  };

  const handleAddToClientList = async () => {
    try {
      let successCount = 0;
      let errorCount = 0;
      let alreadyExistsCount = 0;
      
      for (const roomId of selectedRooms) {
        try {
          const response = await axios.post(`${API}/chat/room/add-to-client-list`, {
            room_id: roomId,
            user_id: user.id
          });
          
          if (response.data.message === "Already in client list") {
            alreadyExistsCount++;
          } else {
            successCount++;
          }
        } catch (err) {
          console.error(`Failed to add room ${roomId}:`, err);
          errorCount++;
        }
      }
      
      // Show appropriate toast messages
      if (successCount > 0) {
        toast.success(`${successCount} chat${successCount > 1 ? 's' : ''} added to client list!`);
      }
      
      if (alreadyExistsCount > 0) {
        toast.info(`${alreadyExistsCount} chat${alreadyExistsCount > 1 ? 's are' : ' is'} already in client list`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} chat${errorCount > 1 ? 's' : ''}`);
      }
      
      setShowClientListModal(false);
      setSelectionMode(false);
      setSelectedRooms([]);
      
    } catch (error) {
      console.error('Failed to add to client list:', error);
      toast.error('Failed to add to client list. Please try again.');
    }
  };


  // Mini component to show bank preview in copy popup
  const CopyPopupBankPreview = ({ customerId, phone }) => {
    const [bankData, setBankData] = React.useState(null);
    React.useEffect(() => {
      axios.get(`${API}/clients/registered`).then(res => {
        const client = res.data.find(c => c.id === customerId || c.phone === phone);
        if (client) setBankData(client);
      }).catch(() => {});
    }, [customerId, phone]);
    if (!bankData) return <p className="text-xs text-gray-400">Loading bank details...</p>;
    if (!bankData.bank_name && !bankData.bank_account_number) return <p className="text-xs text-gray-400">No bank details saved</p>;
    return (
      <div className="text-xs text-gray-600 space-y-0.5">
        {bankData.bank_name && <p>Bank: {bankData.bank_name}</p>}
        {bankData.account_holder_name && <p>Holder: {bankData.account_holder_name}</p>}
        {bankData.bank_account_number && <p>A/c: {bankData.bank_account_number}</p>}
        {bankData.ifsc_code && <p>IFSC: {bankData.ifsc_code}</p>}
        {bankData.upi_id && <p>UPI: {bankData.upi_id}</p>}
      </div>
    );
  };

  // Mobile PWA — WhatsApp-style layout renders in a totally separate component.
  // Desktop and mobile-when-inside-a-chat / archive / other-tab keep the
  // original layout below unchanged.
  const useMobileWA = !isDesktop && !selectedChatId && bottomTab === 'chats'
    && ['deposit','withdrawal','support','inquiry','archive'].includes(activeTab);
  if (useMobileWA) {
    // Point 60 — PWA filter-chip badges count unread CHATS (not messages),
    // scoped to the current chat_type section and MB filter, mirroring what
    // selecting that chip would actually show.
    const mbScopedRoomsForBadges = rooms.filter(r => {
      if (r.is_archived) return false;
      if (r.chat_type !== activeTab) return false;
      if (mbFilter !== 'all' && r.mb_setup !== mbFilter) return false;
      return true;
    });
    const unreadTabBadge = mbScopedRoomsForBadges.filter(r => (unreadCounts[r.id] || 0) > 0).length;
    const favouritesTabBadge = mbScopedRoomsForBadges.filter(r => r.is_favourite && (unreadCounts[r.id] || 0) > 0).length;
    const groupsTabBadge = (groups || []).filter(g => (g.unread_count || 0) > 0).length;
    return (
      <MobileWhatsAppAdminView
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        filterTab={filterTab}
        setFilterTab={setFilterTab}
        filteredRooms={filteredRooms}
        unreadCounts={unreadCounts}
        rooms={rooms}
        unreadTabBadge={unreadTabBadge}
        favouritesTabBadge={favouritesTabBadge}
        groupsTabBadge={groupsTabBadge}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleChatClick={handleChatClick}
        handleLogout={handleLogout}
        user={user}
        loadRooms={loadRooms}
        loadUnreadCounts={loadUnreadCounts}
        loadInquiryData={loadInquiryData}
        supportSubTab={supportSubTab}
        setSupportSubTab={setSupportSubTab}
        mbFilter={mbFilter}
        setMbFilter={setMbFilter}
        mbOptions={['all', ...liveMBs]}
        selectionMode={selectionMode}
        selectedRooms={selectedRooms}
        cancelSelection={cancelSelection}
        handleLongPressStart={handleLongPressStart}
        handleLongPressEnd={handleLongPressEnd}
        handleTouchMove={handleTouchMove}
        toggleRoomSelection={toggleRoomSelection}
        handleToggleArchive={handleToggleArchive}
        handleToggleFavourite={handleToggleFavourite}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        handleDeleteRooms={handleDeleteRooms}
        API={API}
      />
    );
  }

  // Guard: on mobile, arriving with activeTab='groups' must not paint the
  // desktop layout for a frame — the effect above redirects to /groups.
  if (!isDesktop && activeTab === 'groups') {
    return null;
  }

  // Web-view redesign (desktop only) — panel meta for the top-bar colored
  // label + center MB chip. Mobile/PWA branch (MobileWhatsAppAdminView) above
  // is untouched.
  const PANEL_META = {
    deposit: { label: 'Deposit panel', color: '#16a34a' },
    withdrawal: { label: 'Withdraw panel', color: '#e11d48' },
    support: { label: 'Support panel', color: '#2563eb' },
    inquiry: { label: 'Enquiry panel', color: '#7c3aed' },
    archive: { label: 'Archived', color: '#64748b' },
  };
  const panelMeta = PANEL_META[activeTab] || PANEL_META.deposit;
  const centerMB = mbFilter === 'all' ? 'All MBs' : mbFilter;
  // Unread CHATS (not messages) per panel — drives the red count badge on the
  // desktop D/W corner buttons and the S/I top badges.
  const panelUnread = {
    deposit: rooms.filter(r => r.chat_type === 'deposit' && !r.is_archived && (unreadCounts[r.id] || 0) > 0).length,
    withdrawal: rooms.filter(r => r.chat_type === 'withdrawal' && !r.is_archived && (unreadCounts[r.id] || 0) > 0).length,
    support: rooms.filter(r => r.chat_type === 'support' && !r.is_archived && (unreadCounts[r.id] || 0) > 0).length,
    inquiry: rooms.filter(r => r.chat_type === 'inquiry' && !r.is_archived && (unreadCounts[r.id] || 0) > 0).length,
  };
  // Corner (search-row) shortcuts = Deposit/Withdraw toggle (S & I live in the
  // full-width top badge row). Shows the panel(s) not currently active.
  const CORNER_PANELS = [
    { key: 'deposit', letter: 'D', color: '#16a34a' },
    { key: 'withdrawal', letter: 'W', color: '#e11d48' },
  ].filter(p => p.key !== activeTab);

  // Full-width top bar (desktop) / in-column header (mobile). fullWidth adds the
  // center MB+panel-label and the S/I panel toggles.
  const renderTopBar = (fullWidth) => (
    <div className={`${fullWidth ? 'bg-white text-gray-800 border-b border-gray-200' : 'bg-[#008069] text-white'} p-3 flex-shrink-0`}>
      <div className="flex items-center justify-between gap-2">
        <div className="cursor-pointer flex items-center gap-2 min-w-0 flex-shrink" onClick={() => { setSelectedChatId(null); setBottomTab('chats'); navigate('/admin-dashboard'); }}>
          <img src="/icons/icon-128x128.png" alt="DWS" className="w-9 h-9 rounded-full flex-shrink-0" />
          <div className="min-w-0">
            <h1 className={`font-bold truncate text-base sm:text-lg ${fullWidth ? 'text-[#0d7a63]' : ''}`}>DWS Support</h1>
            <p className={`text-[10px] truncate ${fullWidth ? 'text-gray-400' : 'text-white/70'}`}>{fullWidth ? panelMeta.label : 'Admin Panel'}</p>
          </div>
        </div>

        {fullWidth && (
          <div className="flex flex-col items-center gap-1 flex-shrink-0" data-testid="topbar-center-mb">
            <div className="px-4 py-1 rounded-full bg-gray-50 border-2 text-[15px] font-extrabold tracking-wide" style={{ color: panelMeta.color, borderColor: panelMeta.color }}>{centerMB}</div>
            <div className="text-[11px] font-bold px-3 py-0.5 rounded-full text-white" style={{ backgroundColor: panelMeta.color }}>{panelMeta.label}</div>
          </div>
        )}

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {fullWidth && (
            <>
              <button onClick={() => { setActiveTab('support'); setMbFilter('all'); setSelectedChatId(null); }} title="Support panel" data-testid="topbar-badge-S" className={`relative w-9 h-9 rounded-full text-white text-xs font-extrabold flex items-center justify-center ${activeTab === 'support' ? 'ring-2 ring-white' : ''}`} style={{ backgroundColor: '#0d9488' }}>
                S
                {panelUnread.support > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="topbar-badge-S-unread">{panelUnread.support > 99 ? '99+' : panelUnread.support}</span>
                )}
              </button>
              <button onClick={() => { setActiveTab('inquiry'); setMbFilter('all'); setSelectedChatId(null); }} title="Inquiry panel" data-testid="topbar-badge-I" className={`relative w-9 h-9 rounded-full text-white text-xs font-extrabold flex items-center justify-center ${activeTab === 'inquiry' ? 'ring-2 ring-white' : ''}`} style={{ backgroundColor: '#7c3aed' }}>
                I
                {panelUnread.inquiry > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="topbar-badge-I-unread">{panelUnread.inquiry > 99 ? '99+' : panelUnread.inquiry}</span>
                )}
              </button>
            </>
          )}
          <InstantAccessPW variant={fullWidth ? 'light' : 'dark'} />
          <InstantAccessN variant={fullWidth ? 'light' : 'dark'} />
          <InstantAccessG variant={fullWidth ? 'light' : 'dark'} />
          <InstantAccessPT variant={fullWidth ? 'light' : 'dark'} />
          <div className="relative" ref={menuRef}>
            <button onClick={() => setShowMenu(!showMenu)} className={`p-2 rounded-full ${fullWidth ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-white/10'}`}>
              <DotsThreeVertical size={20} weight="bold" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-10 bg-white rounded-lg shadow-2xl border z-[100] w-52 py-1">
                <button onClick={() => { setShowMenu(false); navigate('/groups'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <UsersThree size={18} /> Groups
                </button>
                <button onClick={() => { setShowMenu(false); navigate('/broadcast'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <Megaphone size={18} /> Broadcast
                </button>
                <button onClick={() => { setShowMenu(false); navigate('/client-list'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <Users size={18} /> Clients
                </button>
                <button onClick={() => { setShowMenu(false); setSelectedChatId(null); setBottomTab(bottomTab === 'updates' ? 'chats' : 'updates'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-testid="menu-updates">
                  <ArrowsClockwise size={18} /> {bottomTab === 'updates' ? 'Back to Chats' : 'Updates'}
                </button>
                <button onClick={() => { setShowMenu(false); navigate('/admin/support-analytics'); }} className="w-full px-4 py-2.5 text-left text-sm text-teal-700 hover:bg-teal-50 flex items-center gap-2" data-testid="menu-support-analytics">
                  <ChatCircleDots size={18} /> Support Analytics
                </button>
                <button onClick={async () => { setShowMenu(false); toast.info('Refreshing...'); await loadRooms(); await loadUnreadCounts(); if (activeTab === 'inquiry') await loadInquiryData(); toast.success('Refreshed!'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <ArrowClockwise size={18} /> Refresh
                </button>
                <button onClick={() => { setShowMenu(false); setShowPendingManager(true); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-testid="admin-open-pending-manager-btn">
                  <Broom size={18} /> Pending Manager
                </button>
                <button onClick={() => { setShowMenu(false); navigate('/admin-settings'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <Gear size={18} /> Settings
                </button>
                <div className="border-t my-1" />
                <button onClick={() => { setShowMenu(false); handleLogout(); }} className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                  <SignOut size={18} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Web redesign (desktop) — FULL-WIDTH top bar spanning the whole screen */}
      {isDesktop && renderTopBar(true)}

      <div className={`flex flex-1 min-h-0 ${isDesktop ? 'flex-row' : 'flex-col'}`}>
      <div
        className={`flex flex-col relative bg-white ${isDesktop ? 'border-r border-gray-200 flex-shrink-0 overflow-visible z-40 h-full' : 'flex-1 h-screen'}`}
        style={isDesktop ? { width: `${Math.max(300, listWidth || 380)}px` } : undefined}
      >
        {/* Web redesign (desktop) — draggable divider (always split layout) */}
        {isDesktop && (
          <div
            onMouseDown={startListResize}
            title="Drag to resize"
            data-testid="list-resize-handle"
            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-20 hover:bg-[#008069]/30 active:bg-[#008069]/50"
          />
        )}
        {/* Mobile keeps the header inside the column; desktop uses the full-width bar above */}
        {!isDesktop && renderTopBar(false)}

      {/* Search Bar */}
      <div className="bg-gray-50 p-3 border-b flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <svg
            className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        {/* Web redesign (desktop) — Deposit/Withdraw corner toggle (search row) */}
        {isDesktop && CORNER_PANELS.map(p => (
          <button
            key={p.key}
            onClick={() => { setActiveTab(p.key); setMbFilter('all'); setSelectedChatId(null); }}
            title={`Go to ${p.key} panel`}
            data-testid={`corner-panel-${p.key}`}
            className="relative w-9 h-9 flex-shrink-0 rounded-full text-white text-[14px] font-extrabold flex items-center justify-center shadow hover:opacity-90"
            style={{ backgroundColor: p.color }}
          >
            {p.letter}
            {panelUnread[p.key] > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid={`corner-panel-${p.key}-unread`}>
                {panelUnread[p.key] > 99 ? '99+' : panelUnread[p.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Archive View Header — replaces main tabs when viewing archive */}
      {activeTab === 'archive' ? (
        <div className="bg-white border-b flex items-center gap-3 px-4 py-2.5 flex-shrink-0" data-testid="admin-archive-header">
          <button
            onClick={() => {
              // Restore the tab the user was on before entering Archive
              let prev = 'deposit';
              try {
                const saved = sessionStorage.getItem('admin_tab_before_archive');
                if (saved && ['deposit', 'withdrawal', 'support', 'inquiry'].includes(saved)) prev = saved;
                sessionStorage.removeItem('admin_tab_before_archive');
              } catch (_) {}
              setActiveTab(prev);
            }}
            className="hover:bg-gray-100 p-1.5 rounded-full transition-colors"
            data-testid="admin-archive-back-btn"
            aria-label="Back to chats"
          >
            <ArrowLeft size={22} weight="bold" className="text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-lg">📦</span>
            <h2 className="font-semibold text-gray-800">
              Archived <span className="text-sm text-gray-500 font-normal">({rooms.filter(r => r.is_archived).length})</span>
            </h2>
          </div>
        </div>
      ) : (
      /* Handler-style Tabs (hidden on desktop — panel switching via corner buttons + S/I badges) */
      <div className={`bg-white border-b flex-shrink-0 ${isDesktop ? 'hidden' : 'flex'}`}>
        {[
          { key: 'deposit', label: 'Deposit', count: rooms.filter(r => r.chat_type === 'deposit').length },
          { key: 'withdrawal', label: 'Withdrawal', count: rooms.filter(r => r.chat_type === 'withdrawal').length },
          { key: 'support', label: 'Support', count: rooms.filter(r => r.chat_type === 'support' || r.chat_type === 'inquiry').length },
          { key: 'inquiry', label: 'Inquiry', count: inquiryAnalytics.today }
        ].map(tab => (
          <button
            key={tab.key}
            data-testid={`admin-tab-${tab.key}`}
            aria-pressed={activeTab === tab.key}
            onClick={() => { setActiveTab(tab.key); setMbFilter('all'); setSelectedChatId(null); }}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'text-[#008069] border-b-2 border-[#008069]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>
      )}

      {/* Support Sub-tabs: Support Normal / Inquiry Support */}
      {activeTab === 'support' && (
        <div className="bg-white border-b flex flex-shrink-0">
          <button
            onClick={() => setSupportSubTab('support')}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${
              supportSubTab === 'support'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="support-normal-tab"
          >
            Support Normal ({rooms.filter(r => r.chat_type === 'support').length})
          </button>
          <button
            onClick={() => setSupportSubTab('inquiry')}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${
              supportSubTab === 'inquiry'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="inquiry-support-tab"
          >
            Inquiry Support ({rooms.filter(r => r.chat_type === 'inquiry').length})
          </button>
        </div>
      )}

      {/* Filter Tabs - Same as Handler */}
      {activeTab !== 'inquiry' && (
        <div className="bg-white border-b border-[#e9edef] flex gap-2 px-3 py-2 overflow-x-auto flex-shrink-0">
          {['all', 'unread', 'favourites', 'groups'].map(tab => (
            <button key={tab} onClick={() => tab === 'groups' ? navigate('/groups') : setFilterTab(tab)}
              className={`text-[13px] px-3 py-[5px] rounded-full whitespace-nowrap transition-colors ${filterTab === tab ? 'bg-[#e7fce3] text-[#008069] font-medium' : 'bg-[#f0f2f5] text-[#54656f] hover:bg-[#e9edef]'}`}>
              {tab === 'all' ? 'All' : tab === 'unread' ? 'Unread' : tab === 'favourites' ? 'Favourites' : 'Groups'}
            </button>
          ))}
        </div>
      )}

      {/* MB Setup Tabs — Deposit / Withdrawal / Support */}
      {['deposit', 'withdrawal', 'support'].includes(activeTab) && filterTab !== 'groups' && (
        <div className={`bg-white border-b border-blue-100 flex items-center gap-2 px-3 py-2 flex-shrink-0 relative ${isDesktop ? '' : 'overflow-x-auto'}`} data-testid="mb-tabs-admin">
          <button
            onClick={() => { if (isDesktop) { setShowMbDropdown(v => !v); } else { setMbFilter('all'); } }}
            className={`text-[12px] px-3 py-1 rounded-full font-semibold whitespace-nowrap transition-colors ${
              mbFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
            data-testid="mb-tab-all"
          >
            {isDesktop
              ? `${mbFilter === 'all' ? `All Setups (${rooms.filter(r => r.chat_type === activeTab && !r.is_archived).length})` : mbFilter} ▾`
              : `All Setups (${rooms.filter(r => r.chat_type === activeTab && !r.is_archived).length})`}
          </button>
          {isDesktop && showMbDropdown && (
            <div className="absolute left-3 top-11 z-[60] bg-white border rounded-lg shadow-xl py-1 w-48 max-h-80 overflow-auto" data-testid="mb-dropdown">
              <button onClick={() => { setMbFilter('all'); setShowMbDropdown(false); }} className={`w-full text-left px-3 py-2 text-[13px] hover:bg-blue-50 ${mbFilter === 'all' ? 'text-blue-700 font-bold' : 'text-gray-700'}`}>
                All Setups ({rooms.filter(r => r.chat_type === activeTab && !r.is_archived).length})
              </button>
              {liveMBs.map((mb) => {
                const c = rooms.filter(r => r.chat_type === activeTab && !r.is_archived && r.mb_setup === mb).length;
                return (
                  <button key={mb} onClick={() => { setMbFilter(mb); setShowMbDropdown(false); }} className={`w-full text-left px-3 py-2 text-[13px] hover:bg-blue-50 ${mbFilter === mb ? 'text-blue-700 font-bold' : 'text-gray-700'}`} data-testid={`mb-drop-${mb}`}>
                    {mb} ({c})
                  </button>
                );
              })}
            </div>
          )}
          {!isDesktop && liveMBs.map((mb) => {
            const bucket = rooms.filter(r => r.chat_type === activeTab && !r.is_archived && r.mb_setup === mb);
            const count = bucket.length;
            // Point 30 — badge is a count of unread CHATS in this MB, not a
            // sum of unread messages across them.
            const unread = bucket.filter(r => (unreadCounts[r.id] || 0) > 0).length;
            const isActive = mbFilter === mb;
            return (
              <button
                key={mb}
                onClick={() => setMbFilter(mb)}
                className={`relative text-[12px] px-3 py-1 rounded-full font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  isActive ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                data-testid={`mb-tab-${mb}`}
              >
                <span>{mb} ({count})</span>
                {unread > 0 && (
                  <span
                    className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-white text-red-600' : 'bg-red-500 text-white'
                    }`}
                    data-testid={`mb-unread-${mb}`}
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </button>
            );
          })}
          {/* "G" chip — dropdown listing MB-wise Withdraw Groups (mobile only;
              desktop web uses the G badge in the full-width top bar instead) */}
          {!isDesktop && withdrawGroups.length > 0 && (
            <>
              <button
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setGMenuPos({ top: r.bottom + 4, left: r.left });
                  setShowWithdrawGroupsMenu(v => !v);
                }}
                className="px-2.5 py-1 rounded-full text-[12px] font-bold whitespace-nowrap bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1"
                data-testid="withdraw-group-shortcut-admin"
                title="Withdraw Groups"
              >
                G ▾
              </button>
              {showWithdrawGroupsMenu && (
                <>
                  <div className="fixed inset-0 z-[999]" onClick={() => setShowWithdrawGroupsMenu(false)} />
                  <div
                    className="fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[1000] min-w-[210px] py-1"
                    style={{ top: gMenuPos.top, left: gMenuPos.left }}
                    data-testid="withdraw-groups-menu-admin"
                  >
                    <div className="px-3 py-1.5 text-[10px] font-bold text-amber-600 uppercase border-b border-gray-100">Withdraw Groups</div>
                    {withdrawGroups.map(g => (
                      <button
                        key={g.id}
                        onClick={() => {
                          setShowWithdrawGroupsMenu(false);
                          const from = window.location.pathname + window.location.search + window.location.hash;
                          navigate(`/groups/${g.id}`, {
                            state: { from, fromState: { selectedChatId, activeTab } }
                          });
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-amber-50 text-xs font-semibold text-gray-800 flex items-center justify-between"
                        data-testid={`withdraw-group-mb-admin-${g.mb_setup || 'legacy'}`}
                      >
                        <span>{g.name}</span>
                        {g.last_message_at && <span className="text-[9px] text-gray-400">●</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
          <button
            onClick={() => setShowBulkMB(true)}
            className="ml-auto shrink-0 text-[12px] px-3 py-1 rounded-full font-semibold bg-teal-600 text-white hover:bg-teal-700 whitespace-nowrap"
            data-testid="bulk-mb-open-admin"
          >
            Bulk Assign
          </button>
        </div>
      )}
      {showBulkMB && (
        <BulkAssignMBModal
          open={showBulkMB}
          onClose={() => setShowBulkMB(false)}
          clients={rooms
            .filter(r => r.chat_type === activeTab && !r.is_archived)
            .map(r => ({
              id: r.customer_id,
              name: r.customer_name,
              phone: r.customer_phone,
              mb_setup: r.mb_setup,
              unread_count: unreadCounts[r.id] || 0,
            }))}
          actorId={user?.id}
          onDone={(ids, mbValue) => {
            setRooms(prev => prev.map(r => (
              ids.includes(r.customer_id) ? { ...r, mb_setup: mbValue } : r
            )));
          }}
        />
      )}

      {bottomTab === 'updates' ? (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setBottomTab('chats')} className="p-1.5 rounded-full hover:bg-gray-200 text-gray-600" data-testid="updates-back-btn">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <h2 className="text-xl font-bold text-gray-800">Updates</h2>
          </div>
          <StatusManagement user={user} />
        </div>
      ) : (
      <div ref={chatListScrollRef} onScroll={persistScroll} className="flex-1 overflow-y-auto" data-testid="admin-chat-list-scroll">
        {/* Archive Section - WhatsApp Style */}
        {rooms.filter(r => r.is_archived).length > 0 && activeTab !== 'archive' && (
          <div
            onClick={() => {
              // Remember current tab so Back-from-archive returns here
              try { sessionStorage.setItem('admin_tab_before_archive', activeTab); } catch (_) {}
              setActiveTab('archive');
            }}
            className="bg-white border-b px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">📦</span>
              </div>
              <div>
                <div className="font-semibold text-gray-800">Archived</div>
                <div className="text-sm text-gray-500">
                  {rooms.filter(r => r.is_archived).length} chats
                </div>
              </div>
            </div>
            <div className="text-gray-400">›</div>
          </div>
        )}

        {/* Selection Mode Header */}
        {selectionMode && (
          <div className="sticky top-0 bg-green-500 text-white p-3 flex items-center justify-between z-10">
            <div className="flex items-center gap-2">
              <button onClick={cancelSelection} className="hover:bg-green-600 p-1 rounded">
                ← Back
              </button>
              <span className="font-semibold">{selectedRooms.length} selected</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleTogglePinnedChat}
                disabled={selectedRooms.length === 0}
                className="bg-gray-600 hover:bg-gray-700 px-3 py-1 rounded disabled:opacity-50 text-sm"
                data-testid="admin-selection-pin-btn"
              >
                📌 {selectedRooms.every(rid => rooms.find(r => r.id === rid)?.is_pinned_chat) ? 'Unpin' : 'Pin'}
              </button>
              <button
                onClick={handleToggleArchive}
                disabled={selectedRooms.length === 0}
                className="bg-gray-600 hover:bg-gray-700 px-3 py-1 rounded disabled:opacity-50 text-sm"
              >
                📦 {activeTab === 'archive' ? 'Unarchive' : 'Archive'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={selectedRooms.length === 0}
                className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded disabled:opacity-50 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        )}

        {activeTab === 'inquiry' ? (
          <InquiryPanel user={user} onOpenChat={handleChatClick} />
        ) : false ? (
          /* Inquiry Analytics Tab (legacy, disabled — replaced by InquiryPanel) */
          <div className="p-4 space-y-4">
            {/* Analytics Cards - Clickable */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => { setInquiryFilter('today'); setSelectedDate(''); }}
                className={`bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-3 text-white text-left transition-all ${
                  inquiryFilter === 'today' && !selectedDate ? 'ring-4 ring-orange-300 scale-105' : 'hover:scale-102 hover:shadow-lg'
                }`}
              >
                <p className="text-xs opacity-80">Today</p>
                <p className="text-2xl font-bold">{inquiryAnalytics.today}</p>
                {inquiryFilter === 'today' && !selectedDate && <p className="text-xs mt-1">✓ Selected</p>}
              </button>
              <button
                onClick={() => { setInquiryFilter('7days'); setSelectedDate(''); }}
                className={`bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-3 text-white text-left transition-all ${
                  inquiryFilter === '7days' && !selectedDate ? 'ring-4 ring-purple-300 scale-105' : 'hover:scale-102 hover:shadow-lg'
                }`}
              >
                <p className="text-xs opacity-80">Last 7 Days</p>
                <p className="text-2xl font-bold">{inquiryAnalytics.last_7_days}</p>
                {inquiryFilter === '7days' && !selectedDate && <p className="text-xs mt-1">✓ Selected</p>}
              </button>
              <button
                onClick={() => { setInquiryFilter('total'); setSelectedDate(''); }}
                className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 text-white text-left transition-all ${
                  inquiryFilter === 'total' && !selectedDate ? 'ring-4 ring-blue-300 scale-105' : 'hover:scale-102 hover:shadow-lg'
                }`}
              >
                <p className="text-xs opacity-80">Total</p>
                <p className="text-2xl font-bold">{inquiryAnalytics.total}</p>
                {inquiryFilter === 'total' && !selectedDate && <p className="text-xs mt-1">✓ Selected</p>}
              </button>
            </div>

            {/* Inquiry Link */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm font-medium text-green-800 mb-2">📎 Shareable Inquiry Link</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/inquiry-chat`}
                  readOnly
                  className="flex-1 text-xs bg-white border rounded px-2 py-1"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/inquiry-chat`);
                    toast.success('Link copied!');
                  }}
                  className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Date Filter - Professional Calendar */}
            <div className="bg-white rounded-lg border p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  📅 Filter by Date
                </span>
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate('')}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                  >
                    ✕ Clear Filter
                  </button>
                )}
              </div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50 focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all cursor-pointer"
                style={{ colorScheme: 'light' }}
              />
              {selectedDate && (
                <p className="text-xs text-orange-600 mt-2 text-center">
                  Showing inquiries for: {new Date(selectedDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
            </div>

            {/* Inquiry List */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-3 border-b bg-gradient-to-r from-orange-50 to-purple-50">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  👥 {selectedDate 
                    ? `Inquiries on ${new Date(selectedDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}` 
                    : inquiryFilter === 'today' 
                    ? "Today's Inquiries"
                    : inquiryFilter === '7days'
                    ? 'Last 7 Days Inquiries'
                    : 'All Inquiries'}
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                    {inquiryList.length}
                  </span>
                </h3>
              </div>
              {inquiryList.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <div className="text-4xl mb-2">📭</div>
                  <p>No inquiries {selectedDate ? 'on this date' : inquiryFilter === 'today' ? 'today' : 'found'}</p>
                </div>
              ) : (
                <div className="divide-y max-h-[60vh] overflow-y-auto pb-20">
                  {inquiryList.map((inquiry) => (
                    <div
                      key={inquiry.inquiry_id}
                      className="p-3 hover:bg-orange-50 cursor-pointer transition-colors active:bg-orange-100"
                      onClick={() => inquiry.chat_room_id && navigate(`/admin-chat/${inquiry.chat_room_id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                            {inquiry.client_number || inquiry.guest_name?.replace('Client ', '')}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">{inquiry.guest_name}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <span>🌐</span> {inquiry.ip_address}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {new Date(inquiry.timestamp).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </p>
                          <p className="text-xs text-purple-600 font-medium">
                            💬 {inquiry.messages_count || 0} msgs
                          </p>
                          {inquiry.chat_room_id && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-1">
                              👁️ View
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Chats size={64} weight="thin" />
            <p className="mt-4">
              {searchQuery ? `No results found for "${searchQuery}"` : 
               activeTab === 'groups' ? 'No groups yet' :
               activeTab === 'support' && supportSubTab === 'inquiry' ? 'No inquiry chats yet' :
               `No ${activeTab} chats yet`}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredRooms
              .slice(0, visibleCount)
              .map((room) => {
                const pal = avatarPalFor(room.customer_name || 'C');
                const useLightAvatar = isDesktop && activeTab !== 'groups' && !room.customer_profile_pic;
                return (
                <div
                  key={room.id}
                  data-testid={`admin-room-${room.id}`}
                  onClick={() => {
                    if (selectionMode) {
                      toggleRoomSelection(room.id);
                    } else {
                      if (activeTab === 'groups' || filterTab === 'groups' || room.is_group) {
                        handleGroupClick(room.id);
                      } else {
                        handleChatClick(room.id);
                      }
                    }
                  }}
                  onTouchStart={(e) => !selectionMode && handleLongPressStart(room.id, e)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleLongPressEnd}
                  onMouseDown={(e) => !selectionMode && handleLongPressStart(room.id, e)}
                  onMouseMove={handleTouchMove}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  className={`px-3 py-[10px] hover:bg-[#f5f6f6] cursor-pointer transition-colors active:bg-[#e9edef] ${
                    selectedRooms.includes(room.id)
                      ? 'bg-[#e7fce3]'
                      : (isDesktop && selectedChatId === room.id)
                        ? 'bg-[#f0f2f5] shadow-inner'
                        : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {selectionMode && (
                      <div className="flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={selectedRooms.includes(room.id)}
                          onChange={() => toggleRoomSelection(room.id)}
                          className="w-5 h-5 text-green-600"
                        />
                      </div>
                    )}
                    {/* Avatar - Handler style */}
                    <div
                      className={`w-12 h-12 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center font-bold text-lg ${useLightAvatar ? '' : 'text-white'}`}
                      style={
                        activeTab === 'groups'
                          ? room.profile_pic
                            ? { backgroundImage: `url(${API}${room.profile_pic})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#4ade80' }
                            : { background: 'linear-gradient(135deg, #4ade80, #16a34a)' }
                          : room.customer_profile_pic
                            ? { backgroundImage: `url(${room.customer_profile_pic?.startsWith('http') ? room.customer_profile_pic : API + room.customer_profile_pic})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#00a884' }
                            : useLightAvatar
                              ? { backgroundColor: pal.bg, color: pal.fg }
                              : { backgroundColor: '#00a884' }
                      }
                    >
                      {activeTab === 'groups'
                        ? !room.profile_pic && <span>{room.name?.charAt(0) || 'G'}</span>
                        : !room.customer_profile_pic && <span style={useLightAvatar ? { color: pal.fg } : undefined} className={useLightAvatar ? '' : 'text-white'}>{(room.customer_name || 'C')[0].toUpperCase()}</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {activeTab !== 'groups' && room.is_pinned_chat && (
                            <PushPin size={12} weight="fill" className="text-gray-400 flex-shrink-0" title="Pinned" data-testid={`pinned-icon-${room.id}`} />
                          )}
                          {activeTab !== 'groups' && room.is_priority && (
                            <span title="VIP Priority" className="text-amber-500 text-sm" data-testid={`vip-star-list-${room.id}`}>★</span>
                          )}
                          <h3 className="font-medium text-[#111b21] truncate text-[16px]">
                            {activeTab === 'groups' ? (room.name || 'Group') : (room.customer_name || 'Customer')}
                          </h3>
                          {activeTab !== 'groups' && room.customer_flag && (
                            <FlagIcons flag={room.customer_flag} size={12} title={room.customer_flag === 'red' ? 'Suspicious' : 'Premium'} />
                          )}
                          {activeTab !== 'groups' && <MBSetupBadge value={room.mb_setup} />}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {activeTab !== 'groups' && (
                            <>
                              <button onClick={(e) => { e.stopPropagation(); copyClientDetails(room); }} className="p-0.5 hover:bg-gray-100 rounded" title="Copy client details">
                                <Copy size={14} className="text-gray-400 hover:text-blue-500" />
                              </button>
                              {room.customer_phone && (
                                <button onClick={(e) => { e.stopPropagation(); openWhatsApp(room.customer_phone, room.id); }} className="p-0.5 hover:bg-gray-100 rounded" title="Open WhatsApp">
                                  <WhatsappLogo size={14} className="text-gray-400 hover:text-green-500" />
                                </button>
                              )}
                            </>
                          )}
                          <span className={`text-[11.5px] ${unreadCounts[room.id] > 0 ? 'text-[#00a884] font-medium' : 'text-[#667781]'}`}>
                            {formatChatTime(room.updated_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-[2px] gap-2">
                        <p className="text-[13.5px] text-[#667781] truncate flex-1 min-w-0">
                          {truncatePreview(room.last_message || room.customer_phone || '')}
                        </p>
                        {unreadCounts[room.id] > 0 && (
                          <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ml-2 flex-shrink-0">
                            {unreadCounts[room.id] > 99 ? '99+' : unreadCounts[room.id]}
                          </div>
                        )}
                        {/* Session-42: pending SR pill on admin chat list */}
                        {(room.pending_sr_count || 0) > 0 && (
                          <div
                            className="bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[22px] h-[18px] px-1 flex items-center justify-center ml-1 flex-shrink-0"
                            title={`${room.pending_sr_count} pending request${room.pending_sr_count === 1 ? '' : 's'}`}
                            data-testid={`admin-chat-pending-sr-${room.id}`}
                          >🎫{room.pending_sr_count}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            {/* Progressive-render sentinel — loads more when scrolled into view */}
            {visibleCount < filteredRooms.length && (
              <div
                ref={chatListLoadMoreRef}
                className="py-3 flex items-center justify-center text-[11px] text-gray-500"
                data-testid="admin-chatlist-load-more"
              >
                <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></span>
                Loading more chats… ({filteredRooms.length - visibleCount} remaining)
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Delete Chats?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete {selectedRooms.length} chat{selectedRooms.length > 1 ? 's' : ''}? 
              This will only remove {selectedRooms.length > 1 ? 'them' : 'it'} from your admin panel.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRooms}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Details Popup */}
      {copyPopupChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCopyPopupChat(null)}>
          <div className="bg-white rounded-xl max-w-sm w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="copy-details-popup">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Copy Details</h3>
            
            {/* Login Details */}
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-gray-700">Login Details</span>
                <button
                  onClick={async () => {
                    let text = `Username: ${copyPopupChat.customer_name || 'N/A'}\nPhone: ${copyPopupChat.customer_phone || 'N/A'}\nPassword: Abcd1234`;
                    try {
                      const settingsRes = await axios.get(`${API}/settings`);
                      const loginLink = settingsRes.data?.customer_login_link;
                      if (loginLink) text += `\nLink: ${loginLink}`;
                    } catch (e) {}
                    navigator.clipboard.writeText(text);
                    toast.success('Login details copied!', { duration: 1500 });
                    setCopyPopupChat(null);
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-600">Username: {copyPopupChat.customer_name || 'N/A'}</p>
              <p className="text-xs text-gray-600">Phone: {copyPopupChat.customer_phone || 'N/A'}</p>
              <p className="text-xs text-gray-600">Password: Abcd1234</p>
            </div>

            {/* Bank Details */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-gray-700">Bank Details</span>
                <button
                  onClick={async () => {
                    try {
                      const customerId = copyPopupChat.customer_id;
                      const res = await axios.get(`${API}/clients/registered`);
                      const client = res.data.find(c => c.id === customerId || c.phone === copyPopupChat.customer_phone);
                      if (!client || (!client.bank_name && !client.bank_account_number)) {
                        toast.error('No bank details found'); return;
                      }
                      let text = `User Name: ${client.username || 'N/A'}\n\nA/c Holder Name: ${client.account_holder_name || client.client_name || 'N/A'}\n\nBank Account No: ${client.bank_account_number || 'N/A'}\n\nIFSC Code: ${client.ifsc_code || 'N/A'}\n\nBank Name: ${client.bank_name || 'N/A'}`;
                      if (client.address) text += `\n\nAddress: ${client.address}`;
                      if (client.upi_id) text += `\n\nUPI ID: ${client.upi_id}`;
                      navigator.clipboard.writeText(text);
                      toast.success('Bank details copied!', { duration: 1500 });
                      setCopyPopupChat(null);
                    } catch (e) { toast.error('Failed to fetch bank details'); }
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <CopyPopupBankPreview customerId={copyPopupChat.customer_id} phone={copyPopupChat.customer_phone} />
            </div>

            <button onClick={() => setCopyPopupChat(null)} className="w-full py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Close</button>
          </div>
        </div>
      )}

      {/* P10 (option b) — global bottom navigation REMOVED. Groups via top "G"
          tab; Clients / Settings / Updates / Broadcast via the header 3-dot menu. */}

      </div>{/* End left panel */}

      {/* Right Panel — Desktop split view (Point 9). Always rendered on
          desktop: shows the chat when one is open, else the idle DWS logo.
          Back button inside the chat sets selectedChatId=null → idle logo. */}
      {isDesktop && (
        selectedChatId ? (
          <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
            <AdminChat
              embeddedRoomId={selectedChatId}
              onBackToList={() => setSelectedChatId(null)}
            />
          </div>
        ) : (
          <div
            className="flex-1 min-h-0 flex flex-col items-center justify-center bg-[#f0f2f5] border-l border-gray-200"
            data-testid="admin-idle-panel"
          >
            <img src="/icons/customer/icon-192x192.png" alt="DWS Vault Chat" className="w-24 h-24 opacity-80 mb-4" />
            <h3 className="text-xl font-bold text-gray-700">DWS Vault Chat</h3>
            <p className="text-sm text-gray-400 mt-1">Select a chat to start messaging</p>
          </div>
        )
      )}

      <CustomerDetailsPanel />
      </div>{/* End inner row wrapper */}
      {showPendingManager && (
        <PendingManager user={user} onClose={() => setShowPendingManager(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
